/**
 * The Appendix E enumeration registry.
 *
 * Appendix E is versioned and additive: values are never renumbered or reused, and a deprecated
 * value is marked rather than deleted. Appendix H makes the registry load-bearing rather than
 * documentary, because every enum column is `text` with a check constraint written against it,
 * never a database enum type, so that a new value costs no migration.
 *
 * Each block is a `const` tuple. The tuple is the runtime value the check constraints and the
 * import validators read; the derived union is the compile-time type. One source, both jobs.
 */
export const REMAKE_CAUSES = [
    'lab_error',
    'lab_breakage',
    'shop_rx_error',
    'shop_measurement_error',
    'prescriber_change',
    'non_adapt',
    'frame_warranty',
    'coating_defect',
    'customer_damage',
    'insurer_change',
];
/** `in_house_edging` is registered but v2 (`P-03`). Every v1 job is `external_lab`. */
export const FULFILMENT_MODES = ['external_lab', 'in_house_edging', 'hybrid'];
export const JOB_ORDER_TYPES = [
    'supply_and_fit',
    'frame_to_follow',
    'lens_uncut',
    'lens_with_trace',
    'frame_only',
    'lens_only',
    'glazing_for_trade',
];
export const TENDERS = [
    'cash',
    'card',
    'bank_transfer',
    'wallet',
    'store_credit',
    'gift_card',
    'panel',
    'voucher',
    'cod',
];
export const STOCK_ADJUSTMENT_REASONS = [
    'count_variance',
    'damage',
    'shrinkage',
    'expiry',
    'write_off',
    'revaluation',
    'return_to_vendor',
    'sample',
    'in_house_breakage',
];
export const STOCK_OWNERSHIPS = ['owned', 'consignment', 'on_loan'];
/** `RES-001`: stock has three committed states, not one. */
export const STOCK_COMMITMENTS = ['available', 'reserved', 'allocated', 'sold'];
export const DISCOUNT_REASONS = [
    'promotion',
    'manager',
    'staff_purchase',
    'goodwill',
    'trade_in',
    'panel',
    'price_match',
];
/**
 * Appendix E's `correction_type`. §6.33 `COR-001`.
 *
 * **`duplicate_entry` is registered here and offered nowhere, on purpose.** The registry is
 * Appendix E's and this list does not edit it, so a value the product refuses stays in it; without
 * this line the next person to read the file has no way to tell a deliberate refusal from an
 * oversight, which is `later_todo.md` 45's point.
 *
 * A correction has no ledger consequence in this build, so labelling a sale as a duplicate would
 * leave it counting in every report and sitting on the work queue. The real answers already exist
 * and both reverse rather than annotate: a sale rung up twice is voided, a job raised twice is
 * cancelled, a payment taken twice is refunded. `corrections.service.ts`'s `NOT_OFFERED` carries
 * the sentence each one is refused with.
 */
export const CORRECTION_TYPES = [
    'wrong_customer',
    'wrong_branch',
    'wrong_staff',
    'wrong_job',
    'duplicate_entry',
];
export const RX_TYPES = [
    'distance',
    'near',
    'bifocal',
    'progressive',
    'occupational',
    'safety',
    'contact_lens',
];
export const RX_SOURCES = [
    'in_house',
    'customer_supplied',
    'transcribed',
    'imported',
    'ocr_assisted',
];
/** §5.2 prescription header. Registered in Appendix E as part of `decisions/0003`. */
export const RX_VERIFICATION_STATUSES = ['unverified', 'verified', 'rejected'];
export const PRISM_BASES = ['in', 'out', 'up', 'down', 'degrees'];
export const LENS_DESIGNS = [
    'single_vision',
    'bifocal',
    'trifocal',
    'progressive',
    'occupational',
    'degressive',
];
/**
 * `BR-11`. The spend categories a shop recognises, fixed rather than free text.
 *
 * A P&L whose categories are whatever somebody typed cannot be compared between branches or
 * between months, which is the one thing it exists for. `other` is there so a cost is never
 * unrecordable, and a shop whose `other` is large has a conversation to have.
 *
 * Defined here rather than in `expenses.ts` because the Appendix E registry lives in this file and
 * `expenses.ts` imports the ledger, which would make a cycle.
 */
export const EXPENSE_CATEGORIES = [
    'rent',
    'salaries',
    'utilities',
    'marketing',
    'equipment',
    'professional_fees',
    'transport',
    'maintenance',
    'bank_charges',
    'other',
];
/** `BR-11`'s documented rule for a shared cost. `expenses.ts` carries the reasoning for each. */
export const EXPENSE_ALLOCATIONS = ['direct', 'by_revenue', 'equal'];
export const CONTACT_LENS_MODALITIES = [
    'daily',
    'two_weekly',
    'monthly',
    'quarterly',
    'annual',
];
export const WARRANTY_COMPONENTS = ['frame', 'lens', 'coating', 'non_adapt'];
export const DISPUTE_REASONS = [
    'not_received',
    'not_as_described',
    'damaged',
    'wrong_item',
    'fit_issue',
    'late',
];
/**
 * `MSG-001`: v1 ships **email and in-app only**; WhatsApp and SMS arrive at v3. The registry still
 * carries all five, because Appendix E never removes a value and channel availability is a country
 * and plan gate rather than a registry edit. `MSG-001a` records the risk that this creates in
 * Pakistan, where `PK-021` makes WhatsApp the primary customer channel.
 */
export const MESSAGE_CHANNELS = ['whatsapp', 'sms', 'email', 'push', 'in_app'];
export const AI_TASKS = [
    'rx_ocr',
    'catalogue_capture',
    'price_sheet_parse',
    'message_draft',
    'report_query',
    'frame_match',
    'listing_copy',
    'help',
];
/** §11 `prescription_eye`. OD is the right eye, OS the left. */
export const EYES = ['od', 'os'];
/** `REF-002`. Commission and liability differ per value, which is why it is typed, not free text. */
export const REFRACTION_TESTERS = [
    'in_house_doctor',
    'in_house_refractionist',
    'visiting_optometrist',
    'external_prescriber',
    'customer_supplied_unknown',
];
/** `REF-006`. `referred_out` is a first-class outcome, not an absence of one. */
export const REFRACTION_OUTCOMES = ['prescription_issued', 'no_change', 'referred_out'];
/** `REF-003`. Metric 6/6 is the Pakistan notation and leads for that reason. */
export const VISUAL_ACUITY_NOTATIONS = ['metric_6', 'imperial_20', 'logmar'];
/** §6.39, in the order the table states them. */
export const SALE_TYPES = [
    'complete_spectacles',
    'lenses_only_own_frame',
    'frame_only',
    'single_lens_replacement',
    'plano_sunglasses',
    'prescription_sunglasses',
    'ready_readers',
    'contact_lenses',
    'cl_solutions',
    'repair_or_adjustment',
    'spare_parts',
    'eye_test_only',
];
/** `SAL-011`. See `sale-type.ts` for which type sits where, and why five are undecided. */
export const RETURNS_POSITIONS = [
    'returnable',
    'made_to_specification',
    'policy_dependent',
];
/** `ROLE-002`. Permissions are a matrix over resource × action (Appendix B), not a tier. */
export const ROLES = [
    'owner',
    'manager',
    'dispenser',
    'cashier',
    'optician',
    'accountant',
    'read_only',
];
/** `BR-02`. Default is `shared_all` for owned branches; a franchise usually cannot share. */
export const CUSTOMER_VISIBILITIES = ['shared_all', 'shared_group', 'isolated'];
/**
 * `CAT-010`'s material axis. **Material and index are two axes, not one.**
 *
 * `CAT-010` lists them as pairs: "CR-39 1.50, Trivex, polycarbonate 1.59, 1.60, 1.67, 1.74, glass
 * 1.5/1.6/1.7/1.8/1.9". The bare numbers 1.60, 1.67 and 1.74 name an index with no material beside
 * it, because they are high-index plastics. Registering the numbers as materials would leave the
 * highest-selling lenses in the market with no correct value to enter, and would give glass at five
 * different indices a single one. The index is a separate `numeric(4,2)` column.
 */
export const LENS_MATERIALS = [
    'cr39',
    'trivex',
    'polycarbonate',
    'high_index_plastic',
    'glass',
];
/** `CAT-010`'s surfacing axis: "stock / conventional surfaced / freeform digital". */
export const LENS_SURFACINGS = ['stock', 'conventional_surfaced', 'freeform_digital'];
/**
 * `CAT-012`'s eleven treatment kinds, as the requirement enumerates them.
 *
 * **Moved here from `packages/db/src/tenant/catalogue.ts`**, where it was declared beside the
 * table that checks it. That was fine until the counter needed it for a chooser: `apps/counter`
 * depends on `@lensmira/domain` and not on `@lensmira/db`, and importing the schema package into
 * a browser bundle would pull drizzle and postgres in with it. Every other Appendix E vocabulary
 * is already here, and `db` imports this file, so the direction was always this way round.
 */
export const TREATMENT_KINDS = [
    'hard_coat',
    'ar_coating',
    'mirror_coat',
    'photochromic',
    'polarised',
    'tint_solid',
    'tint_gradient',
    'uv',
    'blue_light_filter',
    'hydrophobic',
    'anti_fog',
    'edge_polish',
];
/**
 * What kind of thing wrote a row. `decisions/0005`.
 *
 * Not a foreign key to staff, and that is the whole point. `ADM-005` and `OPS-002` both require an
 * ANUBRIS operator's actions to be "never attributable to the tenant's staff", and an operator has
 * no row in the tenant's staff table; giving them one is exactly the attribution those two forbid.
 *
 * `system` is not a catch-all either. Three non-human writers already exist: `RX-005`'s OCR
 * pre-fill, `TEN-002a`'s provisioning job, and the migration runner, which writes
 * `updated_by = 'tools/migrate'` today.
 */
export const ACTOR_KINDS = ['staff', 'platform_operator', 'system', 'customer'];
/**
 * The registry as data, for the checks that have to walk every block: the check-constraint
 * generator in `packages/db`, and the import validators.
 */
/**
 * Which fiscalization regime an invoice was signed under. `TAX-004`, `PK-002`, `PK-005`.
 *
 * **One member, and that is the scope rather than an oversight.** `V1-SPEC.md` §2 excludes
 * "fiscalization outside Pakistan", so FBR is the only regime v1 implements, and a second member
 * here would be a column value nothing can produce. The column exists so that the second one is an
 * insert rather than a schema change, which is `TAX-004`'s "model the adapter interface to that
 * shape".
 */
export const FISCAL_REGIMES = ['fbr'];
/**
 * Where an invoice's fiscal record has got to. `PK-004`, `TAX-006`.
 *
 * `PK-004` `MUST`: "FBR transmission failures never block the sale. The invoice is issued, the
 * transmission queued, and the pending count is visible." So `pending` is a normal resting state
 * and not an error, `failed` is a transmission that will be retried, and only `confirmed` carries a
 * number anybody may print.
 */
export const FISCAL_RECORD_STATUSES = ['pending', 'confirmed', 'failed'];
/**
 * How a frame holds its lenses. `CAT-001`, `CAT-003`.
 *
 * **These are `CAT-001`'s own three words**: "rim type (full / semi-rimless / rimless)". They are a
 * registry rather than free text because two vocabularies for one thing were already in the
 * repository and one of them silently defeated a guard: `tools/seed` wrote `half_rim` and
 * `frames.ts` asked whether the rim was `semi_rimless`, so `requiresMountingData` answered false
 * for a seeded semi-rimless frame and `assertSendableToLab` let it go to a lab with no drill or
 * groove data at all. `CAT-003` is a `MUST` and it was unenforceable for half its own range.
 */
export const FRAME_RIM_TYPES = ['full_rim', 'semi_rimless', 'rimless'];
/**
 * What a frame is made of. `CAT-001`, as the requirement lists them.
 *
 * "material (acetate / metal / titanium / TR90 / rimless mount)". Five, spelled as the requirement
 * spells them, because a sixth invented here is a value no country pack and no supplier feed knows.
 */
export const FRAME_MATERIALS = [
    'acetate',
    'metal',
    'titanium',
    'tr90',
    'rimless_mount',
];
export const APPENDIX_E_REGISTRY = {
    /** `CAT-001`, `CAT-003`: full, semi-rimless or rimless, and the last two owe the lab mount data. */
    frame_rim_type: FRAME_RIM_TYPES,
    /** `CAT-001`: acetate, metal, titanium, TR90 or a rimless mount. */
    frame_material: FRAME_MATERIALS,
    /** `TAX-004`, `PK-002`: which regime signed an invoice. `V1-SPEC.md` §2 leaves only FBR in v1. */
    fiscal_regime: FISCAL_REGIMES,
    remake_cause: REMAKE_CAUSES,
    fulfilment_mode: FULFILMENT_MODES,
    job_order_type: JOB_ORDER_TYPES,
    tender: TENDERS,
    stock_adjustment_reason: STOCK_ADJUSTMENT_REASONS,
    stock_ownership: STOCK_OWNERSHIPS,
    stock_commitment: STOCK_COMMITMENTS,
    discount_reason: DISCOUNT_REASONS,
    correction_type: CORRECTION_TYPES,
    rx_type: RX_TYPES,
    rx_source: RX_SOURCES,
    rx_verification_status: RX_VERIFICATION_STATUSES,
    prism_base: PRISM_BASES,
    lens_design: LENS_DESIGNS,
    contact_lens_modality: CONTACT_LENS_MODALITIES,
    /** `BR-11`: what a shop spends that is not stock, and the rule for sharing it out. */
    expense_category: EXPENSE_CATEGORIES,
    expense_allocation: EXPENSE_ALLOCATIONS,
    warranty_component: WARRANTY_COMPONENTS,
    dispute_reason: DISPUTE_REASONS,
    message_channel: MESSAGE_CHANNELS,
    ai_task: AI_TASKS,
    eye: EYES,
    refraction_tester: REFRACTION_TESTERS,
    refraction_outcome: REFRACTION_OUTCOMES,
    visual_acuity_notation: VISUAL_ACUITY_NOTATIONS,
    sale_type: SALE_TYPES,
    returns_position: RETURNS_POSITIONS,
    role: ROLES,
    customer_visibility: CUSTOMER_VISIBILITIES,
    actor_kind: ACTOR_KINDS,
    lens_material: LENS_MATERIALS,
    lens_surfacing: LENS_SURFACINGS,
};
//# sourceMappingURL=enums.js.map